import React from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Permission,
  PermissionResource,
  PermissionAction,
  PermissionScope,
} from '../../../data/models/permission.model';
import * as uiSlice from '../../../data/ui.slice';
import * as securitySlice from '../../../data/security.slice';
import securityUtils from '../../../services/securityUtils';
import SelectMenu from '../../forms/SelectMenu';
import TextInput from '../../forms/TextInput';
import Checkbox from '../../forms/Checkbox';
import ConfirmationDialog from '../../ConfirmationDialog';

const PREFIX = 'PermissionDialog';

const classes = {
  dialog: `${PREFIX}-dialog`,
  content: `${PREFIX}-content`,
  title: `${PREFIX}-title`,
  actions: `${PREFIX}-actions`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.dialog}`]: {
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  [`& .${classes.title}`]: {
    backgroundColor: 'rgba(1, 16, 18, 0.04)',
  },
  [`& .${classes.actions}`]: {
    backgroundColor: 'rgba(1, 16, 18, 0.04)',
    justifyContent: 'start',
    padding: theme.spacing(2),
  },
}));

interface PermissionDialogProps {
  open: boolean;
  permission?: Permission;
  resources: PermissionResource[];
  actions: PermissionAction[];
  scopes: PermissionScope[];
  close: (saved: boolean) => void;
}

export default function PermissionDialog({
  open,
  permission,
  resources,
  actions,
  scopes,
  close,
}: PermissionDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');
  const title = permission
    ? t('permissions.editPermission')
    : t('permissions.createPermission');

  const saving = useAppSelector(securitySlice.selectSavingUserPermission);
  const saved = useAppSelector(securitySlice.selectSavedUserPermission);
  const savedMessage = useAppSelector(uiSlice.selectSuccessMessage);
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const { control, formState, handleSubmit, reset } = useForm({
    mode: 'onTouched',
  });
  const { isDirty, isValid } = formState;

  React.useEffect(() => {
    if (open) {
      const defaults: any = {
        ...securityUtils.getPermissionDefaults(permission),
      };
      reset(defaults);
    }
  }, [open, permission, resources, actions, scopes]);

  React.useEffect(() => {
    if (saved && savedMessage) {
      close(true);
    }
  }, [saved, savedMessage]);

  const savePermission = React.useCallback(
    (data: any) => {
      dispatch(securitySlice.setSavingUserPermission(false));
      dispatch(securitySlice.setSavedUserPermission(false));
      const newPermission = securityUtils.getPermissionFromFormData(
        permission ? permission.userPermissionID : undefined,
        data,
      );
      if (permission) {
        dispatch(securitySlice.updateUserPermissionAsync(newPermission));
      } else {
        dispatch(securitySlice.createUserPermissionAsync(newPermission));
      }
    },
    [dispatch, permission, resources, actions, scopes],
  );

  const cancelDialog = () => {
    close(false);
    handleConfirmationDialogClose();
  };

  const handleConfirmationDialogOpen = () => {
    setConfirmOpen(true);
  };
  const handleConfirmationDialogClose = () => {
    setConfirmOpen(false);
  };

  const isFormDirty = () => {
    if (isDirty) {
      handleConfirmationDialogOpen();
    } else {
      cancelDialog();
    }
  };

  return (
    <RootDialog
      className={classes.content}
      open={open}
      onClose={isFormDirty}
      fullWidth
      maxWidth="sm">
      <DialogTitle className={classes.title}>{title}</DialogTitle>
      <DialogContent>
        <Box className={classes.content}>
          <SelectMenu
            label={t('permissions.resource')}
            name="ResourceID"
            values={resources}
            valueAttr="permissionResourceID"
            labelAttr="permissionResourceName"
            required={true}
            control={control}
            rules={{ required: requiredFieldMessage }}
          />
          <SelectMenu
            label={t('permissions.action')}
            name="ActionID"
            values={actions}
            valueAttr="permissionActionID"
            labelAttr="permissionActionName"
            required={true}
            control={control}
            rules={{ required: requiredFieldMessage }}
          />
          <SelectMenu
            label={t('permissions.scope')}
            name="ScopeID"
            values={scopes}
            valueAttr="permissionScopeID"
            labelAttr="permissionScopeName"
            required={true}
            control={control}
            rules={{ required: requiredFieldMessage }}
          />
          <TextInput
            label={t('permissions.description')}
            name="Description"
            required={true}
            control={control}
            errors={formState.errors}
            inputProps={{ maxLength: 80 }}
            rules={{
              required: requiredFieldMessage,
              pattern: {
                value: /^[a-zA-Z0-9., ]*$/,
                message: t('common.alphaNumeric'),
              },
            }}
          />
          <Checkbox
            label={t('common.active')}
            name="IsActive"
            control={control}
          />
        </Box>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant="contained" color="secondary" onClick={isFormDirty}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!isValid || !isDirty || saving}
          onClick={handleSubmit(savePermission)}>
          {t('common.save')}
        </Button>
      </DialogActions>
      <ConfirmationDialog
        title={t('common.discardChanges')}
        message={t('common.possibleDataLoss')}
        open={confirmOpen}
        positiveAction={cancelDialog}
        negativeAction={handleConfirmationDialogClose}
      />
    </RootDialog>
  );
}
